// Routines writes — carved from `src/features/workflows/hooks.ts`, which is now a
// delegate.
//
// History: partial extraction (the 918-line source splits into this module and
// its `query` sibling), so `git log --follow` cannot trace it —
// `git log --follow -- src/features/workflows/hooks.ts`.
//
// HEADLESS, and the two app couplings leave by DIFFERENT mechanisms because they
// are different kinds of thing:
//
//   TOASTS -> `meta` (tier T1). All seven `toast(...)` calls under the old root
//   are single static `intl.formatMessage({ defaultMessage, id })` descriptors
//   with no interpolated value and no branch, so the copy travels with the
//   mutation as inert data in `routineToasts.messages.ts` and the app's one
//   `MutationCache` subscriber renders it. The family record had these filed as
//   T2 "six of seven branch or interpolate"; that was re-measured against the
//   source and is not what the file says.
//
//   ANALYTICS -> an app-reaction PORT. `trackWorkflowActioned` reaches
//   `trackWorkEvent`, and `trackWorkflowRunClicked` additionally probes the
//   desktop RPA bridge for `client_capabilities_present`. Neither may live in a
//   package, so each hook fires a typed port exactly where the dispatch fired,
//   with the same properties. The PAYLOAD shape is a contract
//   (`contracts/routines-analytics.types.ts`); only the emit is an app reaction.
//   This is what keeps the delegate bodied — it is app-layer population by the
//   ladder's T4b, not debt with a route.
//
// DESKTOP ATTACHMENT crosses the same way: the app resolves a live roster
// `target_id` or, failing that, triggering-client `client_capabilities`. The
// DECISION the probe feeds — omit both fields when it resolves empty, because
// their presence is the backend's desktop-attached signal — is request
// behaviour and stays here.
//
// SIDE-EFFECT TIMING IS PRESERVED, and it is the part a naive carve loses:
// three hooks hold optimistic `onMutate` snapshots with `onError` rollback over
// the `['workflows', 'list']` / `['workflows', 'schedules-batch']` prefixes,
// `useDeleteWorkflow` fires its analytics from `onSettled` (not `onSuccess`) and
// gates it on `!error`, and the two run hooks set `networkMode: 'always'` on
// purpose. All of it moved intact.
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { WORKFLOW_KEYS } from '../../contracts/keys';
import type {
  RunAnyWorkflowInput,
  WorkflowAnalyticsProps,
} from '../../contracts/routines-analytics.types';
import type {
  AddEventRuleRequest,
  CreateWorkflowRequest,
  SetScheduleRequest,
  ShareWorkflowRequest,
  UnshareWorkflowRequest,
  UpdateWorkflowRequest,
  WorkflowListResponse,
  WorkflowSchedule,
} from '../../contracts/routines.types';
import { routinesClient } from '../api/routines';
import {
  invalidateWorkflowLists,
  invalidateWorkflowRunQueries,
} from '../query/useQueryRoutines';
import { eventTypesLabel, parseRunAnyWorkflowInput } from '../routines/routine-analytics';
import { ROUTINE_TOASTS } from './routineToasts.messages';

/**
 * App-reaction port for the `work_workflow_actioned` event, fired exactly where
 * `trackWorkflowActioned(...)` fired and with the same properties.
 *
 * Optional on every hook: a package consumer that does not care about analytics
 * gets a working mutation, which is the property that makes this a port rather
 * than an injected foreign command.
 */
interface RoutineActionedPort {
  onRoutineActioned?: (props: WorkflowAnalyticsProps) => void;
}

/**
 * The run-click event is narrower than the actioned one and is emitted by a
 * different app helper (it awaits the desktop capability probe to fill
 * `client_capabilities_present`, which is why the property is absent here — the
 * app reaction adds it).
 */
type RunClickedProps = Pick<
  WorkflowAnalyticsProps,
  | 'workflow_id'
  | 'trigger_source'
  | 'trigger_type'
  | 'requires_desktop'
  | 'preset_id'
  | 'outcome'
>;

interface RoutineRunPorts {
  onRunClicked?: (props: RunClickedProps) => void;
  onRunError?: (error: unknown) => void;
  /**
   * Desktop attachment for an on-demand run. Prefer a live roster `target_id`;
   * otherwise triggering-client `client_capabilities`. Empty object on web.
   *
   * The `.catch(() => ({}))` lives on THIS side of the port even though the app
   * helper already has one: attachment is best-effort — a failed probe must
   * still send the run — so the guarantee belongs where the request is built.
   */
  resolveRunRequestExtras?: (queryClient: QueryClient) => Promise<{
    target_id?: string;
    client_capabilities?: unknown;
  }>;
}

const readRunRequestExtras = async (
  resolve: RoutineRunPorts['resolveRunRequestExtras'],
  queryClient: QueryClient
): Promise<{ target_id?: string; client_capabilities?: unknown }> => {
  if (!resolve) return {};
  return resolve(queryClient).catch(() => ({}));
};

// ── Mutations ───────────────────────────────────────────────────

// Installing a preset happens via the raw client in presetInstall.ts —
// the setup wizard defers the install until the user finishes, so there is
// no workflow id to bind a mutation hook to.

export const useInstallPrimaryForConnector = ({
  onRoutineActioned,
}: RoutineActionedPort) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (connector: string) =>
      (await routinesClient.installForConnector(connector)).data,
    onSuccess: (data) => {
      invalidateWorkflowLists(qc);
      for (const workflow of data.workflows) {
        onRoutineActioned?.({
          action: 'installed',
          object_type: 'preset',
          workflow_id: workflow.workflow_id,
          preset_id: workflow.source_template_id ?? undefined,
          requires_desktop: workflow.requires_desktop,
          install_source: 'connector',
        });
      }
    },
  });
};

export const useCreateWorkflow = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateWorkflowRequest) =>
      (await routinesClient.create(body)).data,
    onSuccess: () => invalidateWorkflowLists(qc),
  });
};

export const useUpdateWorkflow = ({
  workflowId,
  onRoutineActioned,
}: RoutineActionedPort & { workflowId: string }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateWorkflowRequest) =>
      (await routinesClient.update(workflowId, body)).data,
    onSuccess: (data, variables) => {
      qc.setQueryData(WORKFLOW_KEYS.detail(workflowId), data);
      invalidateWorkflowLists(qc);
      const shortcutOnly = Object.keys(variables).length === 1 && 'shortcut' in variables;
      if (shortcutOnly) {
        onRoutineActioned?.({
          action: variables.shortcut === '' ? 'shortcut_cleared' : 'shortcut_set',
          object_type: 'workflow',
          workflow_id: workflowId,
        });
        return;
      }
      onRoutineActioned?.({
        action: 'edited',
        object_type: 'workflow',
        workflow_id: workflowId,
      });
    },
  });
};

/**
 * Persist a workflow's keyboard shortcut on the backend. Thin wrapper over
 * {@link useUpdateWorkflow} that encodes the clear contract: passing `''`
 * clears the shortcut (the backend maps `''` -> unset; do not send `null`).
 */
export const useWorkflowShortcut = ({
  workflowId,
  onRoutineActioned,
}: RoutineActionedPort & { workflowId: string }) => {
  const update = useUpdateWorkflow({ workflowId, onRoutineActioned });
  const setShortcut = useCallback(
    (combo: string) => update.mutateAsync({ shortcut: combo }),
    [update]
  );
  const clearShortcut = useCallback(() => update.mutateAsync({ shortcut: '' }), [update]);
  return { setShortcut, clearShortcut, isPending: update.isPending };
};

export const useDeleteWorkflow = ({ onRoutineActioned }: RoutineActionedPort) => {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    string,
    { prev: [readonly unknown[], WorkflowListResponse | undefined][] }
  >({
    mutationFn: async (workflowId) => {
      await routinesClient.remove(workflowId);
    },
    onMutate: async (workflowId) => {
      await qc.cancelQueries({ queryKey: ['workflows', 'list'] });
      const prev = qc.getQueriesData<WorkflowListResponse>({
        queryKey: ['workflows', 'list'],
      });
      qc.setQueriesData<WorkflowListResponse>(
        { queryKey: ['workflows', 'list'] },
        (old) => {
          if (!old) return old;
          const remaining = old.workflows.filter((r) => r.workflow_id !== workflowId);
          return { workflows: remaining, total: remaining.length };
        }
      );
      return { prev };
    },
    onError: (_err, _id, context) => {
      context?.prev.forEach(([key, data]) => qc.setQueryData(key, data));
    },
    onSettled: (_data, error, workflowId) => {
      qc.removeQueries({ queryKey: WORKFLOW_KEYS.detail(workflowId) });
      invalidateWorkflowLists(qc);
      if (!error) {
        onRoutineActioned?.({
          action: 'deleted',
          object_type: 'workflow',
          workflow_id: workflowId,
          outcome: 'succeeded',
        });
      }
    },
  });
};

export const useToggleWorkflowEnabled = ({
  workflowId,
  onRoutineActioned,
}: RoutineActionedPort & { workflowId: string }) => {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    boolean,
    { prev: [readonly unknown[], WorkflowListResponse | undefined][] }
  >({
    meta: ROUTINE_TOASTS.toggleEnabled,
    mutationFn: async (enabled: boolean) =>
      (await routinesClient.update(workflowId, { enabled })).data,
    onMutate: async (enabled) => {
      await qc.cancelQueries({ queryKey: ['workflows', 'list'] });
      const prev = qc.getQueriesData<WorkflowListResponse>({
        queryKey: ['workflows', 'list'],
      });
      qc.setQueriesData<WorkflowListResponse>(
        { queryKey: ['workflows', 'list'] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            workflows: old.workflows.map((r) =>
              r.workflow_id === workflowId ? { ...r, enabled } : r
            ),
          };
        }
      );
      return { prev };
    },
    onError: (_err, _enabled, context) => {
      context?.prev.forEach(([key, data]) => qc.setQueryData(key, data));
    },
    onSuccess: (data, enabled) => {
      qc.setQueryData(WORKFLOW_KEYS.detail(workflowId), data);
      invalidateWorkflowLists(qc);
      onRoutineActioned?.({
        action: enabled ? 'enabled' : 'disabled',
        object_type: 'workflow',
        workflow_id: workflowId,
        outcome: 'succeeded',
      });
    },
  });
};

export const useShareWorkflow = ({
  workflowId,
  onRoutineActioned,
}: RoutineActionedPort & { workflowId: string }) => {
  const qc = useQueryClient();
  return useMutation({
    meta: ROUTINE_TOASTS.share,
    mutationFn: async (body: ShareWorkflowRequest) =>
      (await routinesClient.share(workflowId, body)).data,
    onSuccess: (data) => {
      qc.setQueryData(WORKFLOW_KEYS.detail(workflowId), data);
      invalidateWorkflowLists(qc);
      onRoutineActioned?.({
        action: 'shared',
        object_type: 'workflow',
        workflow_id: workflowId,
        outcome: 'succeeded',
      });
    },
  });
};

export const useUnshareWorkflow = ({
  workflowId,
  onRoutineActioned,
}: RoutineActionedPort & { workflowId: string }) => {
  const qc = useQueryClient();
  return useMutation({
    meta: ROUTINE_TOASTS.unshare,
    mutationFn: async (body: UnshareWorkflowRequest) =>
      (await routinesClient.unshare(workflowId, body)).data,
    onSuccess: (data) => {
      qc.setQueryData(WORKFLOW_KEYS.detail(workflowId), data);
      invalidateWorkflowLists(qc);
      onRoutineActioned?.({
        action: 'unshared',
        object_type: 'workflow',
        workflow_id: workflowId,
        outcome: 'succeeded',
      });
    },
  });
};

export type RunWorkflowVariables =
  | Record<string, string>
  | {
      parameterValues?: Record<string, string>;
      trigger_source?: WorkflowAnalyticsProps['trigger_source'];
      requires_desktop?: boolean;
      preset_id?: string | null;
    };

function isRunWorkflowMeta(input: RunWorkflowVariables): input is {
  parameterValues?: Record<string, string>;
  trigger_source?: WorkflowAnalyticsProps['trigger_source'];
  requires_desktop?: boolean;
  preset_id?: string | null;
} {
  return (
    typeof input === 'object' &&
    input !== null &&
    ('trigger_source' in input ||
      'parameterValues' in input ||
      'requires_desktop' in input ||
      'preset_id' in input)
  );
}

function parseRunWorkflowVariables(input?: RunWorkflowVariables): {
  parameterValues?: Record<string, string>;
  trigger_source: NonNullable<WorkflowAnalyticsProps['trigger_source']>;
  requires_desktop?: boolean;
  preset_id?: string | null;
} {
  if (!input) {
    return { trigger_source: 'run_button' };
  }
  if (isRunWorkflowMeta(input)) {
    return {
      parameterValues: input.parameterValues,
      trigger_source: input.trigger_source ?? 'run_button',
      requires_desktop: input.requires_desktop,
      preset_id: input.preset_id,
    };
  }
  return { parameterValues: input, trigger_source: 'run_button' };
}

export const useRunWorkflow = ({
  workflowId,
  onRunClicked,
  onRunError,
  resolveRunRequestExtras: resolveExtras,
}: RoutineRunPorts & { workflowId: string }) => {
  const qc = useQueryClient();
  return useMutation({
    meta: ROUTINE_TOASTS.run,
    // Run is a user-initiated click: always attempt the request. The default
    // networkMode ('online') silently PAUSES the mutation while
    // `navigator.onLine` reports offline — which lies on macOS under VPN /
    // interface flaps — leaving the play button pending forever with no
    // request, no timeout, and no error. 'always' turns a genuinely dead
    // network into a fast failure → the run-failed toast (same pattern as
    // useMutateStartTauriRecording).
    networkMode: 'always',
    mutationFn: async (variables?: RunWorkflowVariables) => {
      const parsed = parseRunWorkflowVariables(variables);
      return (
        await routinesClient.run(workflowId, {
          parameter_values: parsed.parameterValues,
          ...(await readRunRequestExtras(resolveExtras, qc)),
        })
      ).data;
    },
    onSuccess: (_data, variables) => {
      invalidateWorkflowRunQueries(qc);
      // Sidebar Your routines reads latest_openable_run from the list cache.
      invalidateWorkflowLists(qc);
      const parsed = parseRunWorkflowVariables(variables);
      onRunClicked?.({
        workflow_id: workflowId,
        trigger_source: parsed.trigger_source,
        trigger_type: 'on_demand',
        requires_desktop: parsed.requires_desktop,
        preset_id: parsed.preset_id,
        outcome: 'succeeded',
      });
    },
    onError: (error, variables) => {
      onRunError?.(error);
      const parsed = parseRunWorkflowVariables(variables);
      onRunClicked?.({
        workflow_id: workflowId,
        trigger_source: parsed.trigger_source,
        trigger_type: 'on_demand',
        requires_desktop: parsed.requires_desktop,
        preset_id: parsed.preset_id,
        outcome: 'failed',
      });
    },
  });
};

/** Like `useRunWorkflow` but accepts the workflow ID at call time. */
export const useRunAnyWorkflow = ({
  onRunClicked,
  onRunError,
  resolveRunRequestExtras: resolveExtras,
}: RoutineRunPorts) => {
  const qc = useQueryClient();
  return useMutation({
    meta: ROUTINE_TOASTS.run,
    // See useRunWorkflow: never let the online gate pause a user's click.
    networkMode: 'always',
    mutationFn: async (input: RunAnyWorkflowInput) => {
      const parsed = parseRunAnyWorkflowInput(input);
      // Read off the input, never the analytics parser: the prompt is clinician
      // free text and the linked patient must not ride the attribution payload.
      const composer: {
        prompt?: string;
        attachment_ids?: string[];
        patient_profile_id?: string;
      } = typeof input === 'string' ? {} : input;
      return (
        await routinesClient.run(parsed.workflowId, {
          ...(parsed.session_id ? { session_id: parsed.session_id } : {}),
          ...(composer.patient_profile_id
            ? { patient_profile_id: composer.patient_profile_id }
            : {}),
          ...(composer.prompt?.trim() ? { prompt: composer.prompt } : {}),
          ...(composer.attachment_ids?.length
            ? { attachment_ids: composer.attachment_ids }
            : {}),
          ...(await readRunRequestExtras(resolveExtras, qc)),
        })
      ).data;
    },
    onSuccess: (_data, input) => {
      invalidateWorkflowRunQueries(qc);
      // Sidebar Your routines reads latest_openable_run from the list cache.
      invalidateWorkflowLists(qc);
      const parsed = parseRunAnyWorkflowInput(input);
      onRunClicked?.({
        workflow_id: parsed.workflowId,
        trigger_source: parsed.trigger_source,
        trigger_type: parsed.trigger_type,
        requires_desktop: parsed.requires_desktop,
        preset_id: parsed.preset_id,
        outcome: 'succeeded',
      });
    },
    onError: (error, input) => {
      onRunError?.(error);
      const parsed = parseRunAnyWorkflowInput(input);
      onRunClicked?.({
        workflow_id: parsed.workflowId,
        trigger_source: parsed.trigger_source,
        trigger_type: parsed.trigger_type,
        requires_desktop: parsed.requires_desktop,
        preset_id: parsed.preset_id,
        outcome: 'failed',
      });
    },
  });
};

// ── Schedule mutations ──────────────────────────────────────────

export const useSetWorkflowSchedule = ({
  workflowId,
  onRoutineActioned,
}: RoutineActionedPort & { workflowId: string }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: WORKFLOW_KEYS.schedule(workflowId),
    mutationFn: async (body: SetScheduleRequest) =>
      (await routinesClient.setSchedule(workflowId, body)).data,
    onMutate: async () => {
      const cached = qc.getQueryData<WorkflowSchedule | null>(
        WORKFLOW_KEYS.schedule(workflowId)
      );
      if (cached !== undefined) return { hadSchedule: cached !== null };
      try {
        return {
          hadSchedule: (await routinesClient.getSchedule(workflowId)).data !== null,
        };
      } catch {
        return { hadSchedule: undefined };
      }
    },
    onSuccess: (data, _variables, context) => {
      qc.setQueryData(WORKFLOW_KEYS.schedule(workflowId), data);
      invalidateWorkflowLists(qc);
      onRoutineActioned?.({
        action:
          context?.hadSchedule === true
            ? 'schedule_updated'
            : context?.hadSchedule === false
              ? 'schedule_created'
              : 'edited',
        object_type: 'schedule',
        workflow_id: workflowId,
        has_schedule: true,
        outcome: 'succeeded',
      });
    },
  });
};

export const useDeleteWorkflowSchedule = ({
  workflowId,
  onRoutineActioned,
}: RoutineActionedPort & { workflowId: string }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: WORKFLOW_KEYS.schedule(workflowId),
    mutationFn: async () => {
      await routinesClient.deleteSchedule(workflowId);
    },
    onSuccess: () => {
      qc.setQueryData(WORKFLOW_KEYS.schedule(workflowId), null);
      invalidateWorkflowLists(qc);
      onRoutineActioned?.({
        action: 'schedule_deleted',
        object_type: 'schedule',
        workflow_id: workflowId,
        has_schedule: false,
        outcome: 'succeeded',
      });
    },
  });
};

/**
 * Pause or resume the Temporal schedule linked to a workflow.
 * Pass ``true`` to pause, ``false`` to resume.
 */
export const useToggleWorkflowSchedulePaused = ({
  workflowId,
  onRoutineActioned,
}: RoutineActionedPort & { workflowId: string }) => {
  const qc = useQueryClient();
  return useMutation<
    WorkflowSchedule,
    Error,
    boolean,
    {
      prevSchedule: WorkflowSchedule | null | undefined;
      prevBatch: [readonly unknown[], unknown][];
    }
  >({
    meta: ROUTINE_TOASTS.toggleSchedulePaused,
    mutationFn: async (paused: boolean) =>
      paused
        ? (await routinesClient.pauseSchedule(workflowId)).data
        : (await routinesClient.resumeSchedule(workflowId)).data,
    onMutate: async (paused) => {
      await qc.cancelQueries({ queryKey: WORKFLOW_KEYS.schedule(workflowId) });
      await qc.cancelQueries({ queryKey: ['workflows', 'schedules-batch'] });
      const prevSchedule = qc.getQueryData<WorkflowSchedule | null>(
        WORKFLOW_KEYS.schedule(workflowId)
      );
      const prevBatch = qc.getQueriesData({
        queryKey: ['workflows', 'schedules-batch'],
      });
      if (prevSchedule) {
        qc.setQueryData(WORKFLOW_KEYS.schedule(workflowId), {
          ...prevSchedule,
          paused,
        });
      }
      qc.setQueriesData<Record<string, WorkflowSchedule>>(
        { queryKey: ['workflows', 'schedules-batch'] },
        (old) => {
          if (!old?.[workflowId]) return old;
          return { ...old, [workflowId]: { ...old[workflowId], paused } };
        }
      );
      return { prevSchedule, prevBatch };
    },
    onError: (_err, _paused, context) => {
      if (context) {
        qc.setQueryData(WORKFLOW_KEYS.schedule(workflowId), context.prevSchedule);
        context.prevBatch.forEach(([key, data]) => qc.setQueryData(key, data));
      }
    },
    onSuccess: (data, paused) => {
      qc.setQueryData(WORKFLOW_KEYS.schedule(workflowId), data);
      void qc.invalidateQueries({ queryKey: ['workflows', 'schedules-batch'] });
      onRoutineActioned?.({
        action: paused ? 'schedule_paused' : 'schedule_resumed',
        object_type: 'schedule',
        workflow_id: workflowId,
        outcome: 'succeeded',
      });
    },
  });
};

// ── Event rule mutations ────────────────────────────────────────

export const useAddWorkflowEventRule = ({
  workflowId,
  onRoutineActioned,
}: RoutineActionedPort & { workflowId: string }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AddEventRuleRequest) =>
      (await routinesClient.addEventRule(workflowId, body)).data,
    onSuccess: (_data, body) => {
      void qc.invalidateQueries({ queryKey: WORKFLOW_KEYS.eventRules(workflowId) });
      void qc.invalidateQueries({ queryKey: ['workflows', 'event-rules-batch'] });
      onRoutineActioned?.({
        action: 'event_rule_added',
        object_type: 'workflow',
        workflow_id: workflowId,
        event_type: body.event_type,
        has_event_rules: true,
        outcome: 'succeeded',
      });
    },
  });
};

export const useDeleteWorkflowEventRule = ({
  workflowId,
  onRoutineActioned,
}: RoutineActionedPort & { workflowId: string }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ruleId: string) => {
      await routinesClient.deleteEventRule(workflowId, ruleId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: WORKFLOW_KEYS.eventRules(workflowId) });
      void qc.invalidateQueries({ queryKey: ['workflows', 'event-rules-batch'] });
      onRoutineActioned?.({
        action: 'event_rule_deleted',
        object_type: 'workflow',
        workflow_id: workflowId,
        outcome: 'succeeded',
      });
    },
  });
};

/**
 * Replace all event rules (delete existing, add new). Non-atomic; if add phase
 * fails, the error toast warns the user.
 *
 * TODO: Replace with a backend atomic PUT endpoint (add-before-delete or
 * transactional replace) to avoid data loss when the add phase fails after
 * deletes succeed.
 */
export const useSetWorkflowEventRules = ({
  workflowId,
  onRoutineActioned,
}: RoutineActionedPort & { workflowId: string }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: WORKFLOW_KEYS.eventRules(workflowId),
    meta: ROUTINE_TOASTS.setEventRules,
    mutationFn: async (rules: AddEventRuleRequest[]) => {
      const existing = (await routinesClient.listEventRules(workflowId)).data;
      await Promise.all(
        existing.event_rules.map((r) =>
          routinesClient.deleteEventRule(workflowId, r.rule_id)
        )
      );
      return Promise.all(
        rules.map((body) =>
          routinesClient.addEventRule(workflowId, body).then((r) => r.data)
        )
      );
    },
    onSuccess: (_data, rules) => {
      void qc.invalidateQueries({ queryKey: WORKFLOW_KEYS.eventRules(workflowId) });
      invalidateWorkflowLists(qc);
      onRoutineActioned?.({
        action: 'event_rule_replaced',
        object_type: 'workflow',
        workflow_id: workflowId,
        has_event_rules: rules.length > 0,
        event_types: eventTypesLabel(rules.map((rule) => rule.event_type)),
        outcome: 'succeeded',
      });
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: WORKFLOW_KEYS.eventRules(workflowId) });
      void qc.invalidateQueries({ queryKey: ['workflows', 'event-rules-batch'] });
    },
  });
};
